import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../../services/axiosInstance";

// Sidebar-only: always fetches today's data into a separate Redux key so it
// never overwrites the date-scoped summary used by Dashboard / Analytics.
export const fetchLiveSummary = createAsyncThunk("analytics/fetchLiveSummary", async (_, thunkAPI) => {
  try {
    const today = new Date().toISOString().slice(0, 10);
    const res = await axiosInstance.get(`/Analytics/summary?dateFrom=${today}&dateTo=${today}`);
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const fetchSummary = createAsyncThunk("analytics/fetchSummary", async (params, thunkAPI) => {
  try {
    const q = new URLSearchParams();
    if (params?.dateFrom) q.set("dateFrom", params.dateFrom);
    if (params?.dateTo)   q.set("dateTo",   params.dateTo);
    const qs = q.toString();
    const res = await axiosInstance.get(`/Analytics/summary${qs ? `?${qs}` : ""}`);
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const fetchPeriodSummary = createAsyncThunk("analytics/fetchPeriodSummary", async ({ dateFrom = "", dateTo = "" } = {}, thunkAPI) => {
  try {
    const q = new URLSearchParams();
    if (dateFrom) q.set("dateFrom", dateFrom);
    if (dateTo)   q.set("dateTo",   dateTo);
    const qs = q.toString();
    const res = await axiosInstance.get(`/Analytics/summary${qs ? `?${qs}` : ""}`);
    return res.data;
  } catch (err) {
    if (err.response?.status === 403) return thunkAPI.rejectWithValue({ blocked: true });
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const fetchCalls = createAsyncThunk("analytics/fetchCalls", async (params = {}, thunkAPI) => {
  try {
    const { page = 1, size = 50, section = "", status = "", dateFrom = "", dateTo = "", caller = "", sortBy = "", sortDir = "", answeredAgent = "" } = params;
    const query = new URLSearchParams();
    query.set("page", page);
    query.set("size", size);
    if (section)       query.set("section",       section);
    if (status)        query.set("status",        status);
    if (dateFrom)      query.set("dateFrom",      dateFrom);
    if (dateTo)        query.set("dateTo",        dateTo);
    if (caller)        query.set("caller",        caller);
    if (sortBy)        query.set("sortBy",        sortBy);
    if (sortDir)       query.set("sortDir",       sortDir);
    if (answeredAgent) query.set("answeredAgent", answeredAgent);
    const res = await axiosInstance.get(`/Analytics/calls?${query}`);
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const fetchAttempts = createAsyncThunk("analytics/fetchAttempts", async (uniqueId, thunkAPI) => {
  try {
    const res = await axiosInstance.get(`/Analytics/calls/${uniqueId}/attempts`);
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const fetchAgents = createAsyncThunk("analytics/fetchAgents", async (_, thunkAPI) => {
  try {
    const res = await axiosInstance.get("/Analytics/agents");
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const addAgent = createAsyncThunk("analytics/addAgent", async (payload, thunkAPI) => {
  try {
    const res = await axiosInstance.post("/Analytics/agents", payload);
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
  }
});

export const toggleAgent = createAsyncThunk("analytics/toggleAgent", async (id, thunkAPI) => {
  try {
    const res = await axiosInstance.patch(`/Analytics/agents/${id}/toggle`);
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const fetchExtensions = createAsyncThunk("analytics/fetchExtensions", async (_, thunkAPI) => {
  try {
    const res = await axiosInstance.get("/Analytics/extensions");
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const deleteAgent = createAsyncThunk("analytics/deleteAgent", async (id, thunkAPI) => {
  try {
    await axiosInstance.delete(`/Analytics/agents/${id}`);
    return id;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const bulkToggleAgents = createAsyncThunk("analytics/bulkToggleAgents", async ({ ids, active }, thunkAPI) => {
  try {
    const res = await axiosInstance.post("/Analytics/agents/bulk-toggle", { ids, active });
    return { ids, active: res.data.active };
  } catch (err) {
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const bulkDeleteAgents = createAsyncThunk("analytics/bulkDeleteAgents", async (ids, thunkAPI) => {
  try {
    await axiosInstance.post("/Analytics/agents/bulk-delete", { ids });
    return ids;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const fetchTranscript = createAsyncThunk("analytics/fetchTranscript", async (uniqueId, thunkAPI) => {
  try {
    const res = await axiosInstance.get(`/Analytics/calls/${uniqueId}/transcript`);
    return res.data;
  } catch (err) {
    if (err.response?.status === 404) return null;
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const fetchCallSummaryAI = createAsyncThunk("analytics/fetchCallSummaryAI", async (uniqueId, thunkAPI) => {
  try {
    const res = await axiosInstance.get(`/Analytics/calls/${uniqueId}/summary`);
    return res.data;
  } catch (err) {
    if (err.response?.status === 404) return null;
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const fetchSipAccounts = createAsyncThunk("analytics/fetchSipAccounts", async (_, thunkAPI) => {
  try {
    const res = await axiosInstance.get("/sip/accounts");
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const fetchNotes = createAsyncThunk("analytics/fetchNotes", async (uniqueId, thunkAPI) => {
  try {
    const res = await axiosInstance.get(`/Analytics/calls/${uniqueId}/notes`);
    return res.data;
  } catch (err) { return thunkAPI.rejectWithValue(err.message); }
});

export const addNote = createAsyncThunk("analytics/addNote", async ({ uniqueId, content }, thunkAPI) => {
  try {
    const res = await axiosInstance.post(`/Analytics/calls/${uniqueId}/notes`, { content });
    return res.data;
  } catch (err) { return thunkAPI.rejectWithValue(err.response?.data?.message || err.message); }
});

export const deleteNote = createAsyncThunk("analytics/deleteNote", async ({ uniqueId, noteId }, thunkAPI) => {
  try {
    await axiosInstance.delete(`/Analytics/calls/${uniqueId}/notes/${noteId}`);
    return noteId;
  } catch (err) { return thunkAPI.rejectWithValue(err.message); }
});

export const fetchTransfers = createAsyncThunk("analytics/fetchTransfers", async (params = {}, thunkAPI) => {
  try {
    const { page = 1, size = 50, dateFrom = "", dateTo = "", status = "", type = "" } = params;
    const q = new URLSearchParams();
    q.set("page", page); q.set("size", size);
    if (dateFrom) q.set("dateFrom", dateFrom);
    if (dateTo)   q.set("dateTo",   dateTo);
    if (status)   q.set("status",   status);
    if (type)     q.set("type",     type);
    const res = await axiosInstance.get(`/Analytics/transfers?${q}`);
    return res.data;
  } catch (err) { return thunkAPI.rejectWithValue(err.message); }
});

export const fetchVoicemails = createAsyncThunk("analytics/fetchVoicemails", async (params = {}, thunkAPI) => {
  try {
    const { page = 1, size = 25, dateFrom = '', dateTo = '' } = params;
    const q = new URLSearchParams({ page, size });
    if (dateFrom) q.set('from', dateFrom);
    if (dateTo)   q.set('to',   dateTo);
    const res = await axiosInstance.get(`/Analytics/voicemails?${q}`);
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const fetchAgentPerformance = createAsyncThunk("analytics/fetchAgentPerformance", async ({ dateFrom = "", dateTo = "" } = {}, thunkAPI) => {
  try {
    const q = new URLSearchParams();
    if (dateFrom) q.set("dateFrom", dateFrom);
    if (dateTo)   q.set("dateTo",   dateTo);
    const qs = q.toString();
    const res = await axiosInstance.get(`/Analytics/agent-performance${qs ? `?${qs}` : ""}`);
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const fetchCallFlowExtensions = createAsyncThunk("analytics/fetchCallFlowExtensions", async (_, thunkAPI) => {
  try {
    const res = await axiosInstance.get("/Analytics/callflow-extensions");
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const fetchExtensionPool = createAsyncThunk("analytics/fetchExtensionPool", async (_, thunkAPI) => {
  try {
    const res = await axiosInstance.get("/extensions/pool");
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const fetchTopCallers = createAsyncThunk("analytics/fetchTopCallers", async ({ dateFrom = "", dateTo = "", limit = 10 } = {}, thunkAPI) => {
  try {
    const q = new URLSearchParams({ limit });
    if (dateFrom) q.set("dateFrom", dateFrom);
    if (dateTo)   q.set("dateTo",   dateTo);
    const res = await axiosInstance.get(`/Analytics/top-callers?${q}`);
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const fetchMissedCalls = createAsyncThunk("analytics/fetchMissedCalls", async ({ dateFrom = "", dateTo = "", page = 1, size = 50 } = {}, thunkAPI) => {
  try {
    const q = new URLSearchParams({ page, size });
    if (dateFrom) q.set("dateFrom", dateFrom);
    if (dateTo)   q.set("dateTo",   dateTo);
    const res = await axiosInstance.get(`/Analytics/missed-calls?${q}`);
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const fetchDispositionSummary = createAsyncThunk("analytics/fetchDispositionSummary", async ({ dateFrom = "", dateTo = "" } = {}, thunkAPI) => {
  try {
    const q = new URLSearchParams();
    if (dateFrom) q.set("dateFrom", dateFrom);
    if (dateTo)   q.set("dateTo",   dateTo);
    const qs = q.toString();
    const res = await axiosInstance.get(`/Analytics/disposition-summary${qs ? `?${qs}` : ""}`);
    return res.data;
  } catch (err) { return thunkAPI.rejectWithValue(err.message); }
});

export const fetchSectionPerformance = createAsyncThunk("analytics/fetchSectionPerformance", async ({ dateFrom = "", dateTo = "" } = {}, thunkAPI) => {
  try {
    const q = new URLSearchParams();
    if (dateFrom) q.set("dateFrom", dateFrom);
    if (dateTo)   q.set("dateTo",   dateTo);
    const qs = q.toString();
    const res = await axiosInstance.get(`/Analytics/section-performance${qs ? `?${qs}` : ""}`);
    return res.data;
  } catch (err) { return thunkAPI.rejectWithValue(err.message); }
});

export const fetchTransferSummary = createAsyncThunk("analytics/fetchTransferSummary", async ({ dateFrom = "", dateTo = "", status = "", type = "" } = {}, thunkAPI) => {
  try {
    const q = new URLSearchParams();
    if (dateFrom) q.set("dateFrom", dateFrom);
    if (dateTo)   q.set("dateTo",   dateTo);
    if (status)   q.set("status",   status);
    if (type)     q.set("type",     type);
    const res = await axiosInstance.get(`/Analytics/transfers/summary?${q}`);
    return res.data;
  } catch (err) { return thunkAPI.rejectWithValue(err.message); }
});

export const fetchHourlyStats = createAsyncThunk("analytics/fetchHourlyStats", async ({ dateFrom = "", dateTo = "" } = {}, thunkAPI) => {
  try {
    const q = new URLSearchParams();
    if (dateFrom) q.set("dateFrom", dateFrom);
    if (dateTo)   q.set("dateTo",   dateTo);
    const qs = q.toString();
    const res = await axiosInstance.get(`/Analytics/hourly${qs ? `?${qs}` : ""}`);
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.message);
  }
});

const analyticsSlice = createSlice({
  name: "analytics",
  initialState: {
    liveSummary: null,
    liveSummaryStatus: "idle",
    summary: null,
    summaryStatus: "idle",
    periodSummary: null,
    periodSummaryStatus: "idle",
    calls: [],
    callsTotal: 0,
    callsPage: 1,
    callsStatus: "idle",
    attempts: [],
    attemptsStatus: "idle",
    transcript: null,
    transcriptStatus: "idle",
    callSummaryAI: null,
    callSummaryAIStatus: "idle",
    agents: [],
    agentsStatus: "idle",
    extensions: [],
    extensionsStatus: "idle",
    sipAccounts: [],
    sipAccountsStatus: "idle",
    callFlowExtensions: [],
    callFlowExtensionsStatus: "idle",
    extensionPool: [],
    extensionPoolStatus: "idle",
    hourly: [],
    hourlyStatus: "idle",
    topCallers: [],
    topCallersStatus: "idle",
    missedCalls: [],
    missedCallsTotal: 0,
    missedCallsPage: 1,
    missedCallsStatus: "idle",
    agentPerf: [],
    agentPerfStatus: "idle",
    sectionPerf: [],
    sectionPerfStatus: "idle",
    dispositionSummary: [],
    dispositionSummaryStatus: "idle",
    voicemails: [],
    voicemailsTotal: 0,
    voicemailsPage: 1,
    voicemailsStatus: "idle",
    notes: [],
    notesStatus: "idle",
    transfers: [],
    transfersTotal: 0,
    transfersPage: 1,
    transfersStatus: "idle",
    transferSummary: null,
    transferSummaryStatus: "idle",
    analyticsBlocked: false,
    error: null,
  },
  reducers: {
    setCallsPage: (state, action) => { state.callsPage = action.payload; },
    clearAttempts: (state) => { state.attempts = []; },
    clearCallDetail: (state) => {
      state.attempts = [];
      state.attemptsStatus = "idle";
      state.transcript = null;
      state.transcriptStatus = "idle";
      state.callSummaryAI = null;
      state.callSummaryAIStatus = "idle";
      state.notes = [];
      state.notesStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLiveSummary.pending,   (s)    => { s.liveSummaryStatus = "loading"; })
      .addCase(fetchLiveSummary.fulfilled, (s, a) => { s.liveSummaryStatus = "success"; s.liveSummary = a.payload; })
      .addCase(fetchLiveSummary.rejected,  (s, a) => { s.liveSummaryStatus = "error";   s.error = a.payload; })

      .addCase(fetchSummary.pending, (s) => { s.summaryStatus = "loading"; })
      .addCase(fetchSummary.fulfilled, (s, a) => { s.summaryStatus = "success"; s.summary = a.payload; })
      .addCase(fetchSummary.rejected, (s, a) => { s.summaryStatus = "error"; s.error = a.payload; })

      .addCase(fetchPeriodSummary.pending,   (s)    => { s.periodSummaryStatus = "loading"; s.analyticsBlocked = false; })
      .addCase(fetchPeriodSummary.fulfilled, (s, a) => { s.periodSummaryStatus = "success"; s.periodSummary = a.payload; s.analyticsBlocked = false; })
      .addCase(fetchPeriodSummary.rejected,  (s, a) => {
        if (a.payload?.blocked) { s.periodSummaryStatus = "error"; s.analyticsBlocked = true; }
        else                    { s.periodSummaryStatus = "error"; s.error = a.payload; }
      })

      .addCase(fetchCalls.pending, (s) => { s.callsStatus = "loading"; })
      .addCase(fetchCalls.fulfilled, (s, a) => {
        s.callsStatus = "success";
        s.calls = a.payload.items;
        s.callsTotal = a.payload.total;
        s.callsPage = a.payload.page;
      })
      .addCase(fetchCalls.rejected, (s, a) => { s.callsStatus = "error"; s.error = a.payload; })

      .addCase(fetchAttempts.pending, (s) => { s.attemptsStatus = "loading"; s.attempts = []; })
      .addCase(fetchAttempts.fulfilled, (s, a) => { s.attemptsStatus = "success"; s.attempts = a.payload; })
      .addCase(fetchAttempts.rejected, (s, a) => { s.attemptsStatus = "error"; s.error = a.payload; })

      .addCase(fetchAgents.pending, (s) => { s.agentsStatus = "loading"; })
      .addCase(fetchAgents.fulfilled, (s, a) => { s.agentsStatus = "success"; s.agents = a.payload; })
      .addCase(fetchAgents.rejected, (s, a) => { s.agentsStatus = "error"; s.error = a.payload; })

      .addCase(fetchExtensions.pending,   (s)    => { s.extensionsStatus = "loading"; })
      .addCase(fetchExtensions.fulfilled, (s, a) => { s.extensionsStatus = "success"; s.extensions = a.payload; })
      .addCase(fetchExtensions.rejected,  (s, a) => { s.extensionsStatus = "error"; s.error = a.payload; })

      .addCase(addAgent.fulfilled, (s, a) => { s.agents.push(a.payload); })

      .addCase(toggleAgent.fulfilled, (s, a) => {
        const idx = s.agents.findIndex((ag) => ag.id === a.payload.id);
        if (idx !== -1) s.agents[idx].active = a.payload.active;
      })

      .addCase(deleteAgent.fulfilled, (s, a) => {
        s.agents = s.agents.filter((ag) => ag.id !== a.payload);
      })

      .addCase(bulkToggleAgents.fulfilled, (s, a) => {
        const { ids, active } = a.payload;
        const idSet = new Set(ids);
        s.agents.forEach(ag => { if (idSet.has(ag.id)) ag.active = active; });
      })

      .addCase(bulkDeleteAgents.fulfilled, (s, a) => {
        const idSet = new Set(a.payload);
        s.agents = s.agents.filter(ag => !idSet.has(ag.id));
      })

      .addCase(fetchSipAccounts.pending,   (s)    => { s.sipAccountsStatus = "loading"; })
      .addCase(fetchSipAccounts.fulfilled, (s, a) => { s.sipAccountsStatus = "success"; s.sipAccounts = a.payload; })
      .addCase(fetchSipAccounts.rejected,  (s, a) => { s.sipAccountsStatus = "error"; s.error = a.payload; })

      .addCase(fetchCallFlowExtensions.pending,   (s)    => { s.callFlowExtensionsStatus = "loading"; })
      .addCase(fetchCallFlowExtensions.fulfilled, (s, a) => { s.callFlowExtensionsStatus = "success"; s.callFlowExtensions = a.payload; })
      .addCase(fetchCallFlowExtensions.rejected,  (s, a) => { s.callFlowExtensionsStatus = "error"; s.error = a.payload; })

      .addCase(fetchExtensionPool.pending,   (s)    => { s.extensionPoolStatus = "loading"; })
      .addCase(fetchExtensionPool.fulfilled, (s, a) => { s.extensionPoolStatus = "success"; s.extensionPool = a.payload; })
      .addCase(fetchExtensionPool.rejected,  (s, a) => { s.extensionPoolStatus = "error"; s.error = a.payload; })

      .addCase(fetchHourlyStats.pending,   (s)    => { s.hourlyStatus = "loading"; })
      .addCase(fetchHourlyStats.fulfilled, (s, a) => { s.hourlyStatus = "success"; s.hourly = a.payload; })
      .addCase(fetchHourlyStats.rejected,  (s, a) => { s.hourlyStatus = "error";   s.error = a.payload; })

      .addCase(fetchTopCallers.pending,   (s)    => { s.topCallersStatus = "loading"; })
      .addCase(fetchTopCallers.fulfilled, (s, a) => { s.topCallersStatus = "success"; s.topCallers = a.payload; })
      .addCase(fetchTopCallers.rejected,  (s, a) => { s.topCallersStatus = "error";   s.error = a.payload; })

      .addCase(fetchMissedCalls.pending,   (s)    => { s.missedCallsStatus = "loading"; })
      .addCase(fetchMissedCalls.fulfilled, (s, a) => {
        s.missedCallsStatus = "success";
        s.missedCalls      = a.payload.items;
        s.missedCallsTotal = a.payload.total;
        s.missedCallsPage  = a.payload.page;
      })
      .addCase(fetchMissedCalls.rejected,  (s, a) => { s.missedCallsStatus = "error"; s.error = a.payload; })

      .addCase(fetchTranscript.pending,   (s)    => { s.transcriptStatus = "loading"; s.transcript = null; })
      .addCase(fetchTranscript.fulfilled, (s, a) => { s.transcriptStatus = "success"; s.transcript = a.payload; })
      .addCase(fetchTranscript.rejected,  (s, a) => { s.transcriptStatus = "error";   s.error = a.payload; })

      .addCase(fetchCallSummaryAI.pending,   (s)    => { s.callSummaryAIStatus = "loading"; s.callSummaryAI = null; })
      .addCase(fetchCallSummaryAI.fulfilled, (s, a) => { s.callSummaryAIStatus = "success"; s.callSummaryAI = a.payload; })
      .addCase(fetchCallSummaryAI.rejected,  (s, a) => { s.callSummaryAIStatus = "error";   s.error = a.payload; })

      .addCase(fetchAgentPerformance.pending,   (s)    => { s.agentPerfStatus = "loading"; })
      .addCase(fetchAgentPerformance.fulfilled, (s, a) => { s.agentPerfStatus = "success"; s.agentPerf = a.payload; })
      .addCase(fetchAgentPerformance.rejected,  (s, a) => { s.agentPerfStatus = "error";   s.error = a.payload; })

      .addCase(fetchSectionPerformance.pending,   (s)    => { s.sectionPerfStatus = "loading"; })
      .addCase(fetchSectionPerformance.fulfilled, (s, a) => { s.sectionPerfStatus = "success"; s.sectionPerf = a.payload; })
      .addCase(fetchSectionPerformance.rejected,  (s, a) => { s.sectionPerfStatus = "error";   s.error = a.payload; })

      .addCase(fetchDispositionSummary.pending,   (s)    => { s.dispositionSummaryStatus = "loading"; })
      .addCase(fetchDispositionSummary.fulfilled, (s, a) => { s.dispositionSummaryStatus = "success"; s.dispositionSummary = a.payload; })
      .addCase(fetchDispositionSummary.rejected,  (s, a) => { s.dispositionSummaryStatus = "error";   s.error = a.payload; })

      .addCase(fetchNotes.pending,   (s)    => { s.notesStatus = "loading"; })
      .addCase(fetchNotes.fulfilled, (s, a) => { s.notesStatus = "success"; s.notes = a.payload; })
      .addCase(fetchNotes.rejected,  (s, a) => { s.notesStatus = "error"; s.error = a.payload; })
      .addCase(addNote.fulfilled,    (s, a) => { s.notes.push(a.payload); })
      .addCase(deleteNote.fulfilled, (s, a) => { s.notes = s.notes.filter((n) => n.id !== a.payload); })

      .addCase(fetchTransfers.pending,   (s)    => { s.transfersStatus = "loading"; })
      .addCase(fetchTransfers.fulfilled, (s, a) => {
        s.transfersStatus = "success";
        s.transfers       = a.payload.items;
        s.transfersTotal  = a.payload.total;
        s.transfersPage   = a.payload.page;
      })
      .addCase(fetchTransfers.rejected,  (s, a) => { s.transfersStatus = "error"; s.error = a.payload; })

      .addCase(fetchTransferSummary.pending,   (s)    => { s.transferSummaryStatus = "loading"; })
      .addCase(fetchTransferSummary.fulfilled, (s, a) => { s.transferSummaryStatus = "success"; s.transferSummary = a.payload; })
      .addCase(fetchTransferSummary.rejected,  (s, a) => { s.transferSummaryStatus = "error"; s.error = a.payload; })

      .addCase(fetchVoicemails.pending,   (s)    => { s.voicemailsStatus = "loading"; })
      .addCase(fetchVoicemails.fulfilled, (s, a) => {
        s.voicemailsStatus = "success";
        s.voicemails      = a.payload.items;
        s.voicemailsTotal = a.payload.total;
        s.voicemailsPage  = a.payload.page;
      })
      .addCase(fetchVoicemails.rejected,  (s, a) => { s.voicemailsStatus = "error"; s.error = a.payload; });
  },
});

export const { setCallsPage, clearAttempts, clearCallDetail } = analyticsSlice.actions;
export default analyticsSlice.reducer;
